import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import { styled, useTheme } from '@mui/material/styles';
import { useCallback } from 'react';
import { Checkbox } from '@/fields/Checkbox';
import { IItemType } from '../TransferList';

type IContent = {
    disabled?: boolean;
    error?: boolean;
    items: IItemType[];
    checked: string[];
    readOnly?: boolean;
    onToggle: (id: string) => void;
};

const StyledContent = styled(Paper)(
    ({ theme }) => `
    border: 1px transparent solid;
    height: 100%;
    overflow: auto;
    border: 1px transparent solid;
    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background-color: transparent;
    }

    &::-webkit-scrollbar-thumb {
        margin: 0 2px;
        background-color: ${theme.palette.primary.main};
        border-radius: ${theme.shape.borderRadius}px;
    }
`
);
const StyledList = styled(List)`
    padding: 0;
`;
const StyledItem = styled(ListItemButton)`
    padding: 0;
`;

type IContentItemProps = Pick<IContent, 'disabled' | 'readOnly' | 'onToggle'> & {
    id: string;
    checked: boolean;
    name: React.ReactNode;
};

const ContentItem = ({ disabled, checked, id, name, readOnly, onToggle }: IContentItemProps) => {
    const handleToggle = useCallback(() => {
        if (onToggle) {
            onToggle(id);
        }
    }, [id, onToggle]);

    return (
        <StyledItem
            key={id}
            role="listitem"
            onClick={handleToggle}
            data-testid={`transferItem-${id}`}
            disabled={disabled}
        >
            <ListItemIcon>
                <Checkbox
                    name={`${id}.checkbox`}
                    value={checked}
                    tabIndex={-1}
                    disableRipple
                    disabled={disabled}
                    readOnly={readOnly}
                    slotProps={{
                        input: {
                            'aria-labelledby': `transfer-list-item-${id}-label`
                        }
                    }}
                />
            </ListItemIcon>
            <ListItemText id={`transfer-list-item-${id}-label`} primary={name} />
        </StyledItem>
    );
};

export const Content = ({ disabled, error, items = [], checked = [], readOnly, onToggle }: IContent) => {
    const theme = useTheme();

    return (
        <StyledContent sx={error ? { borderColor: theme.palette.error.main } : undefined}>
            <StyledList role="list">
                {items.map((item) =>
                    !item.isDivider ? (
                        <ContentItem
                            key={item.id}
                            disabled={disabled || item.disabled}
                            checked={checked.includes(item.id)}
                            id={item.id}
                            name={item.name}
                            readOnly={readOnly}
                            onToggle={onToggle}
                        />
                    ) : (
                        <Divider key={item.id} />
                    )
                )}
            </StyledList>
        </StyledContent>
    );
};
